import React, { useState, useEffect } from 'react';
import { userAPI, userServiceAPI, courseAPI, cacheUtils, adminAPI } from '../../services/api';
import { DataTable } from '../ui/data-table';
import { Button } from '../ui/button';
import CreateUserModal from '../ui/CreateUserModal';
import CreateUserProfileModal from '../ui/CreateUserProfileModal';
import ProfileModal from '../ui/ProfileModal';
import * as XLSX from 'xlsx';

const Users = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({});
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showCreateProfileModal, setShowCreateProfileModal] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [profileData, setProfileData] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [createdUserData, setCreatedUserData] = useState(null);
  
  // User Services state
  const [userServices, setUserServices] = useState([]);
  const [courses, setCourses] = useState([]);
  const [showUserServicesModal, setShowUserServicesModal] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [showCreateServiceModal, setShowCreateServiceModal] = useState(false);
  const [serviceFormData, setServiceFormData] = useState({
    user_id: '',
    course_id: '',
    expired_at: ''
  });
  const [editingServiceId, setEditingServiceId] = useState(null);
  const [deletingUserId, setDeletingUserId] = useState(null);
  
  // Bulk upload state
  const [isBulkUploadModalOpen, setIsBulkUploadModalOpen] = useState(false);
  const [bulkUploadFile, setBulkUploadFile] = useState(null);
  const [bulkUploadProgress, setBulkUploadProgress] = useState({ current: 0, total: 0, status: '' });
  const [isBulkUploading, setIsBulkUploading] = useState(false);
  
  // Password update state
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [selectedUserForPassword, setSelectedUserForPassword] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [updatingPassword, setUpdatingPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const data = await userAPI.getAllUsers();
      setUsers(data?.data || data || []);
    } catch (err) {
      setError('Failed to fetch users');
      console.error('Error fetching users:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (user) => {
    setEditingUser(user.id);
    setFormData(user);
  };
  const handleProfileData = async (user) => {
    try {
      setLoading(true);
      setCurrentUser(user);
      const data = await userAPI.getUserProfile(user.email);
      setProfileData(data?.data || data);
      setShowProfileModal(true);
    } catch (err) {
      // If 401 error, refresh token and retry
      if (err.message && err.message.includes('401')) {
        try {
          console.log('401 error detected, refreshing token...');
          // Call refresh token API
          const refreshResponse = await adminAPI.refreshToken({ token: localStorage.getItem('admin_token') });
          console.log('Token refresh response:', refreshResponse);
          
          // Update tokens in session storage
          if (refreshResponse?.data?.access_token) {
            sessionStorage.setItem('authToken', refreshResponse.data.access_token);
            if (refreshResponse.data.refresh_token) {
              sessionStorage.setItem('refreshToken', refreshResponse.data.refresh_token);
            }
            console.log('Token refreshed successfully, retrying getUserProfile...');
            
            // Retry the getUserProfile call with new token
            const data = await userAPI.getUserProfile(user.email);
            setProfileData(data?.data || data);
            setShowProfileModal(true);
          } else {
            throw new Error('No access token in refresh response');
          }
        } catch (retryErr) {
          setError('Failed to fetch user profile. Please login again.');
          console.error('Error fetching user profile after token refresh:', retryErr);
        }
      } else {
        setError('Failed to fetch user profile');
        console.error('Error fetching user profile:', err);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      console.log('Sending user data', formData);
      await userAPI.updateUser(formData);
      setEditingUser(null);
      setFormData({});
      fetchUsers();
    } catch (err) {
      setError('Failed to update user');
      console.error('Error updating user:', err);
    } finally {
      setEditingUser(null);
      setFormData({});
    }
  };

  const handleCancel = () => {
    setEditingUser(null);
    setFormData({});
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleViewUser = async (userId) => {
    try {
      const userData = await userAPI.getUserById(userId);
      alert(`User Details:\n${JSON.stringify(userData, null, 2)}`);
    } catch (err) {
      setError('Failed to fetch user details');
      console.error('Error fetching user:', err);
    }
  };

  const handleDeleteUser = async (userId, userName) => {
    // Show confirmation dialog
    const isConfirmed = window.confirm(
      `Are you sure you want to delete user "${userName}"? This action cannot be undone.`
    );
    
    if (!isConfirmed) {
      return;
    }

    try {
      setDeletingUserId(userId);
      await userAPI.deleteUser(userId);
      
      // Clear cache and refresh users list
      cacheUtils.clearUserCache();
      await fetchUsers();
      
      // Show success message
      setError(`User "${userName}" has been successfully deleted.`);
      // Note: We're using setError to show success message - you might want to add a success state
    } catch (err) {
      setError('Failed to delete user');
      console.error('Error deleting user:', err);
    } finally {
      setDeletingUserId(null);
    }
  };

  const handleUserCreated = (userData) => {
    setCreatedUserData(userData);
    setShowCreateModal(false);
    setShowCreateProfileModal(true);
    fetchUsers();
  };

  const handleProfileCreated = () => {
    setShowCreateProfileModal(false);
    setCreatedUserData(null);
    fetchUsers();
  };

  // User Services Functions
  const fetchCourses = async () => {
    try {
      const response = await courseAPI.getAllCourses();
      setCourses(response.data || []);
    } catch (err) {
      console.error('Error fetching courses:', err);
    }
  };

  const fetchUserServices = async (userId) => {
    try {
      setLoading(true);
      const response = await userServiceAPI.getUserServicesByUserId(userId);
      setUserServices(response.data || []);
    } catch (err) {
      setError('Failed to fetch user services');
      console.error('Error fetching user services:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleViewUserServices = async (userId) => {
    setSelectedUserId(userId);
    
    // Clear user services cache before fetching fresh data
    cacheUtils.clearUserServiceCache();
    
    await Promise.all([fetchUserServices(userId), fetchCourses()]);
    setShowUserServicesModal(true);
  };

  const handleCreateUserService = async () => {
    try {
      setLoading(true);
      
      // Transform date to required format with default time 23:59:59Z
      const expiredAtFormatted = serviceFormData.expired_at ? 
        `${serviceFormData.expired_at}T23:59:59Z` : 
        null;

      const payload = {
        user_id: parseInt(serviceFormData.user_id),
        course_id: parseInt(serviceFormData.course_id),
        expired_at: expiredAtFormatted
      };

      console.log('Creating user service with payload:', payload);
      console.log('API endpoint:', 'POST /user-services');
      console.log('Full URL would be:', `${process.env.REACT_APP_BASE_URL}/user-services`);
      
      const response = await userServiceAPI.createUserService(payload);
      console.log('User service created successfully:', response);
      
      // Clear cache and refresh user services
      cacheUtils.clearUserServiceCache();
      if (selectedUserId) {
        await fetchUserServices(selectedUserId);
      }
      
      // Reset form
      setServiceFormData({
        user_id: '',
        course_id: '',
        expired_at: ''
      });
      setShowCreateServiceModal(false);
    } catch (err) {
      setError('Failed to create user service');
      console.error('Error creating user service:', err);
      console.error('Error response:', err.response);
      console.error('Error status:', err.response?.status);
      console.error('Error data:', err.response?.data);
      
      // Show more detailed error to user
      if (err.response?.status === 307) {
        setError(`Failed to create user service: 307 Redirect Error. Check API endpoint configuration.`);
      } else {
        setError(`Failed to create user service: ${err.response?.data?.message || err.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateUserService = async (serviceId, updateData) => {
    try {
      setLoading(true);
      await userServiceAPI.updateUserService(serviceId, updateData);
      
      // Refresh user services
      if (selectedUserId) {
        await fetchUserServices(selectedUserId);
      }
      
      setEditingServiceId(null);
    } catch (err) {
      setError('Failed to update user service');
      console.error('Error updating user service:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleExtendUserService = async (serviceId, days) => {
    try {
      setLoading(true);
      await userServiceAPI.extendUserService(serviceId, { days: parseInt(days) });
      
      // Clear cache and refresh user services
      cacheUtils.clearUserServiceCache();
      if (selectedUserId) {
        await fetchUserServices(selectedUserId);
      }
    } catch (err) {
      setError('Failed to extend user service');
      console.error('Error extending user service:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUserService = async (serviceId) => {
    if (!window.confirm('Are you sure you want to delete this user service?')) {
      return;
    }

    try {
      setLoading(true);
      await userServiceAPI.deleteUserService(serviceId);
      
      // Clear cache and refresh user services
      cacheUtils.clearUserServiceCache();
      if (selectedUserId) {
        await fetchUserServices(selectedUserId);
      }
    } catch (err) {
      setError('Failed to delete user service');
      console.error('Error deleting user service:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleServiceInputChange = (field, value) => {
    setServiceFormData(prev => ({ ...prev, [field]: value }));
  };
  
  const handleOpenPasswordModal = (user) => {
    setSelectedUserForPassword(user);
    setNewPassword('');
    setConfirmPassword('');
    setShowNewPassword(false);
    setShowConfirmPassword(false);
    setShowPasswordModal(true);
  };

  const handleUpdatePassword = async () => {
    if (!newPassword || !confirmPassword) {
      alert('Please fill in both password fields.');
      return;
    }

    if (newPassword !== confirmPassword) {
      alert('Passwords do not match. Please try again.');
      return;
    }

    if (newPassword.length < 6) {
      alert('Password must be at least 6 characters long.');
      return;
    }

    try {
      setUpdatingPassword(true);
      
      const passwordData = {
        email: selectedUserForPassword.email,
        password_hash: newPassword
      };

      console.log('Updating password for user:', selectedUserForPassword.email);
      await userAPI.updatePassword(passwordData);
      
      alert(`Password updated successfully for ${selectedUserForPassword.first_name} ${selectedUserForPassword.last_name}`);
      setShowPasswordModal(false);
      setSelectedUserForPassword(null);
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      console.error('Error updating password:', err);
      alert(`Failed to update password: ${err.response?.data?.message || err.message}`);
    } finally {
      setUpdatingPassword(false);
    }
  };
  
  // Bulk Upload Functions
  const downloadExampleFile = () => {
    const exampleData = [
      {
        // CreateUserModal fields (Step 1)
        'username': 'student001',
        'email': 'student001@example.com',
        'password_hash': 'password123',
        'first_name': 'John',
        'last_name': 'Doe',
        'role': 'user',
        // CreateUserProfileModal fields (Step 2) - for students
        'phone_number': '80101054',
        'student_number': 'STU001',
        'rd': 'RD001',
        'course': '1',
        'level': 'A1',
        'mode': 'Online',
        'initials': 'JD',
        'payment': '1000',
        'payment_remaining': '500',
        'exam_date': '2025-12-31',
        'exam_result': 'Pending',
        'image': ''
      },
      {
        // CreateUserModal fields (Step 1)
        'username': 'student002',
        'email': 'student002@example.com',
        'password_hash': 'password123',
        'first_name': 'Jane',
        'last_name': 'Smith',
        'role': 'user',
        // CreateUserProfileModal fields (Step 2) - for students
        'phone_number': '80101055',
        'student_number': 'STU002',
        'rd': 'RD002',
        'course': '1',
        'level': 'A2',
        'mode': 'Offline',
        'initials': 'JS',
        'payment': '1500',
        'payment_remaining': '0',
        'exam_date': '2025-12-31',
        'exam_result': 'Pending',
        'image': ''
      },
      {
        // CreateUserModal fields (Step 1)
        'username': 'instructor001',
        'email': 'instructor001@example.com',
        'password_hash': 'password123',
        'first_name': 'Robert',
        'last_name': 'Johnson',
        'role': 'instructor',
        // CreateUserProfileModal fields (Step 2) - for instructors (minimal fields)
        'phone_number': '80101056',
        'student_number': '',
        'rd': '',
        'course': '',
        'level': '',
        'mode': '',
        'initials': '',
        'payment': '',
        'payment_remaining': '',
        'exam_date': '',
        'exam_result': '',
        'image': ''
      }
    ];

    const worksheet = XLSX.utils.json_to_sheet(exampleData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Users');
    
    // Set column widths
    worksheet['!cols'] = [
      { wch: 15 },  // username
      { wch: 30 },  // email
      { wch: 15 },  // password_hash
      { wch: 15 },  // first_name
      { wch: 15 },  // last_name
      { wch: 12 },  // role
      { wch: 15 },  // phone_number
      { wch: 15 },  // student_number
      { wch: 15 },  // rd
      { wch: 10 },  // course
      { wch: 10 },  // level
      { wch: 10 },  // mode
      { wch: 10 },  // initials
      { wch: 12 },  // payment
      { wch: 18 },  // payment_remaining
      { wch: 15 },  // exam_date
      { wch: 15 },  // exam_result
      { wch: 30 }   // image
    ];

    XLSX.writeFile(workbook, 'users_bulk_upload_template.xlsx');
  };

  const handleBulkUploadFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setBulkUploadFile(file);
    }
  };

  const handleBulkUpload = async () => {
    if (!bulkUploadFile) {
      alert('Please select a file to upload.');
      return;
    }

    try {
      setIsBulkUploading(true);
      setBulkUploadProgress({ current: 0, total: 0, status: 'Reading file...' });

      const reader = new FileReader();
      
      reader.onload = async (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          const worksheet = workbook.Sheets[workbook.SheetNames[0]];
          const jsonData = XLSX.utils.sheet_to_json(worksheet);

          if (jsonData.length === 0) {
            alert('The Excel file is empty or improperly formatted.');
            setIsBulkUploading(false);
            return;
          }

          setBulkUploadProgress({ 
            current: 0, 
            total: jsonData.length, 
            status: `Processing ${jsonData.length} users...` 
          });

          let successCount = 0;
          let errorCount = 0;
          const errors = [];

          for (let i = 0; i < jsonData.length; i++) {
            const row = jsonData[i];
            
            try {
              setBulkUploadProgress({ 
                current: i + 1, 
                total: jsonData.length, 
                status: `Creating user ${i + 1} of ${jsonData.length}...` 
              });

              // Validate required fields (matching CreateUserModal)
              if (!row['username'] || !row['email'] || !row['password_hash']) {
                throw new Error('Missing required fields (username, email, or password_hash)');
              }

              if (!row['first_name'] || !row['last_name']) {
                throw new Error('Missing required fields (first_name or last_name)');
              }

              // Create user account (matching CreateUserModal formData)
              const userData = {
                username: row['username']?.toString().trim(),
                email: row['email']?.toString().trim(),
                password_hash: row['password_hash']?.toString(),
                first_name: row['first_name']?.toString().trim(),
                last_name: row['last_name']?.toString().trim(),
                role: row['role']?.toString().toLowerCase().trim() || 'user'
              };

              await userAPI.createUser(userData);

              // Create user profile if additional data is provided (matching CreateUserProfileModal formData)
              if (row['phone_number']) {
                const profileData = {
                  email: userData.email,
                  first_name: userData.first_name,
                  last_name: userData.last_name,
                  phone_number: row['phone_number']?.toString().trim() || '',
                  student_number: row['student_number']?.toString().trim() || '',
                  rd: row['rd']?.toString().trim() || '',
                  course: row['course']?.toString().trim() || '',
                  level: row['level']?.toString().trim() || '',
                  mode: row['mode']?.toString().trim() || '',
                  class: row['class']?.toString().trim() || '',
                  payment: row['payment'] ? parseInt(row['payment']) : 0,
                  payment_remaining: row['payment_remaining'] ? parseInt(row['payment_remaining']) : 0,
                  student_id: row['student_id']?.toString().trim() || '',
                  initials: row['initials']?.toString().trim() || '',
                  net_id: row['net_id']?.toString().trim() || '',
                  combined_name: row['combined_name']?.toString().trim() || '',
                  class_id: row['class_id']?.toString().trim() || '',
                  exam_date: row['exam_date']?.toString().trim() || new Date().toISOString().split('T')[0],
                  exam_result: row['exam_result']?.toString().trim() || '',
                  image: row['image']?.toString().trim() || ''
                };

                try {
                  await userAPI.createUserProfile(profileData);
                } catch (profileErr) {
                  console.warn(`Profile creation failed for ${userData.email}:`, profileErr);
                  // Don't fail the whole process if profile creation fails
                }
              }

              successCount++;
            } catch (err) {
              errorCount++;
              errors.push({
                row: i + 1,
                username: row['username'] || 'N/A',
                error: err.response?.data?.message || err.message
              });
              console.error(`Error creating user at row ${i + 1}:`, err);
            }
          }

          // Clear cache after bulk upload
          cacheUtils.clearUserCache();
          
          // Refresh users list
          await fetchUsers();

          // Show summary
          let summaryMessage = `Bulk upload completed!\n\n`;
          summaryMessage += `✅ Successfully created: ${successCount} users\n`;
          if (errorCount > 0) {
            summaryMessage += `❌ Failed: ${errorCount} users\n\n`;
            summaryMessage += `Errors:\n`;
            errors.forEach(err => {
              summaryMessage += `Row ${err.row} (${err.username}): ${err.error}\n`;
            });
          }
          alert(summaryMessage);

          // Reset modal state
          setIsBulkUploadModalOpen(false);
          setBulkUploadFile(null);
          setBulkUploadProgress({ current: 0, total: 0, status: '' });
        } catch (err) {
          alert(`Error reading file: ${err.message}`);
        } finally {
          setIsBulkUploading(false);
        }
      };

      reader.onerror = () => {
        alert('Failed to read the file.');
        setIsBulkUploading(false);
      };

      reader.readAsArrayBuffer(bulkUploadFile);
    } catch (err) {
      alert(`Error during bulk upload: ${err.message}`);
      setIsBulkUploading(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto p-8">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading users...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-6xl mx-auto p-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-700">{error}</p>
          <button 
            onClick={setError(null)}
            className="mt-2 px-4 py-2 bg-red-100 text-red-700 rounded hover:bg-red-200"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const columns = [
    {
      accessorKey: "Username",
      header: "Username",
      sortable: true,
      cell: (user) => <div className="text-sm text-gray-500">{user.username }</div>
    },
    {
      accessorKey: "first_name",
      header: "First Name",
      sortable: true,
      cell: (user) => {
        if (editingUser === user.id) {
          return (
            <input
              type="text"
              value={formData.first_name || ''}
              onChange={(e) => handleInputChange('first_name', e.target.value)}
              className="w-full px-2 py-1 border border-input rounded text-sm"
            />
          );
        }
      return <div className="font-medium">{user.first_name}</div>;
      },
    },
    {
      accessorKey: "last_name",
      header: "Last Name",
      sortable: true,
      cell: (user) => {
        if (editingUser === user.id) {
          return (
            <input
              type="text"
              value={formData.last_name || ''}
              onChange={(e) => handleInputChange('last_name', e.target.value)}
              className="w-full px-2 py-1 border border-input rounded text-sm"
            />
          );
        }
      return <div className="font-medium">{user.last_name}</div>;
      },
    },
    {
      accessorKey: "email",
      header: "Email",
      sortable: true,
      cell: (user) => {
        if (editingUser === user.id) {
          return (
            <input
              type="email"
              value={formData.email || ''}
              onChange={(e) => handleInputChange('email', e.target.value)}
              className="w-full px-2 py-1 border border-input rounded text-sm"
            />
          );
        }
        return <div className="text-sm">{user.email}</div>;
      },
    },
    {
      accessorKey: "role",
      header: "Role",
      sortable: true,
      cell: (user) => (
        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
          user.role === 'instructor' 
            ? 'bg-blue-100 text-blue-800' 
            : 'bg-red-100 text-red-800'
        }`}>
          {user.role || 'user'}
        </span>
      ),
    },
    {
      accessorKey: "actions",
      header: "Actions",
      cell: (user) => (
        <div className="flex space-x-2">
          {editingUser === user.id ? (
            <>
              <Button
                size="sm"
                onClick={handleSave}
                className="h-8 px-2"
              >
                Save
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleCancel}
                className="h-8 px-2"
              >
                Cancel
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleViewUser(user.id)}
                className="h-8 px-2"
              >
                View
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleEdit(user)}
                className="h-8 px-2"
              >
                Edit
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleProfileData(user)}
                className="h-8 px-2"
              >
                See Profile
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setCreatedUserData({ 
                    email: user.email,
                    first_name: user.first_name,
                    last_name: user.last_name 
                  });
                  setShowCreateProfileModal(true);
                }}
                className="h-8 px-2 bg-purple-50 hover:bg-purple-100 text-purple-700 border-purple-200"
              >
                Create Profile
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleOpenPasswordModal(user)}
                className="h-8 px-2 bg-yellow-50 hover:bg-yellow-100 text-yellow-700 border-yellow-200"
              >
                Update Password
              </Button>
              {user.role !== 'instructor' && <Button
                variant="outline"
                size="sm"
                onClick={() => handleViewUserServices(user.id)}
                className="h-8 px-2 bg-green-50 hover:bg-green-100 text-green-700"
              >
                Services
              </Button>}
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleDeleteUser(user.id, `${user.first_name} ${user.last_name}` || user.username)}
                disabled={deletingUserId === user.id}
                className="h-8 px-2 bg-red-50 hover:bg-red-100 text-red-700 border-red-200 disabled:opacity-50"
              >
                {deletingUserId === user.id ? (
                  <>
                    <div className="animate-spin w-3 h-3 border border-red-600 border-t-transparent rounded-full mr-1"></div>
                    Deleting...
                  </>
                ) : (
                  'Delete'
                )}
              </Button>
            </>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="max-w-6xl mx-auto p-8">
      <div className="mb-8 border-b border-gray-200 pb-4">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">User Management</h1>
        <p className="text-lg text-gray-600">Manage system users and their permissions</p>
      </div>

      <div className="bg-white shadow-md rounded-lg p-6">
        <div className="mb-4 flex justify-between items-center">
          <h2 className="text-xl font-semibold text-gray-800">Users ({users.length})</h2>
          <div className="flex gap-2">
            <button
              onClick={() => setShowCreateModal(true)}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
            >
              + Create User
            </button>
            <button
              onClick={downloadExampleFile}
              className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors flex items-center gap-2"
            >
              <span>📥</span> Download Template
            </button>
            <button
              onClick={() => setIsBulkUploadModalOpen(true)}
              className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors flex items-center gap-2"
            >
              <span>📤</span> Bulk Upload
            </button>
          </div>
           {/* <Button onClick={() => setShowCreateProfileModal(true)}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors">
            Create User Profile
          </Button> */}
        </div>

        <DataTable
          columns={columns}
          data={users}
          searchKey="first_name"
          searchPlaceholder="Search users by name..."
          loading={loading}
          emptyMessage="No users found. Users will appear here once they are added to the system."
        />
      </div>
      
      <CreateUserModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onUserCreated={handleUserCreated}
      />
      
      <CreateUserProfileModal
        isOpen={showCreateProfileModal}
        onClose={() => setShowCreateProfileModal(false)}
        onUserCreated={handleProfileCreated}
        userBasicData={createdUserData}
      />
      
      <ProfileModal
        isOpen={showProfileModal}
        onClose={() => {
          setShowProfileModal(false);
          setProfileData(null);
          setCurrentUser(null);
        }}
        profileData={profileData}
        userRole={currentUser?.role}
        loading={loading && showProfileModal}
      />

      {/* User Services Modal */}
      {showUserServicesModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold text-gray-900">User Services Management</h2>
              <div className="flex space-x-2">
                <Button
                  onClick={() => {
                    setServiceFormData({ 
                      user_id: selectedUserId, 
                      course_id: '', 
                      expired_at: '' 
                    });
                    setShowCreateServiceModal(true);
                  }}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  + Add Service
                </Button>
                <button
                  onClick={() => {
                    setShowUserServicesModal(false);
                    setSelectedUserId(null);
                    setUserServices([]);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            <div className="space-y-4">
              {userServices.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  No services found for this user.
                </div>
              ) : (
                userServices.map((service) => {
                  const course = courses.find(c => c.id === service.course_id);
                  const isExpired = new Date(service.expired_at) < new Date();
                  const isEditing = editingServiceId === service.id;

                  return (
                    <div key={service.id} className={`border rounded-lg p-4 ${isExpired ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'}`}>
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-4">
                            <div>
                              <p className="font-medium">{course?.title || `Course ID: ${service.course_id}`}</p>
                              <p className="text-sm text-gray-600">
                                Expires: {new Date(service.expired_at).toLocaleDateString()}
                                {isExpired && <span className="text-red-600 ml-2">(Expired)</span>}
                              </p>
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex items-center space-x-2">
                          <input
                            type="number"
                            placeholder="Days"
                            className="w-20 px-2 py-1 border rounded text-sm"
                            onKeyPress={(e) => {
                              if (e.key === 'Enter') {
                                handleExtendUserService(service.id, e.target.value);
                                e.target.value = '';
                              }
                            }}
                          />
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              const days = prompt('Enter number of days to extend:');
                              if (days) {
                                handleExtendUserService(service.id, days);
                              }
                            }}
                            className="text-blue-600 hover:text-blue-800"
                          >
                            Extend
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleDeleteUserService(service.id)}
                            className="text-red-600 hover:text-red-800"
                          >
                            Delete
                          </Button>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      )}

      {/* Create Service Modal */}
      {showCreateServiceModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold text-gray-900">Create User Service</h2>
              <button
                onClick={() => {
                  setShowCreateServiceModal(false);
                  setServiceFormData({ user_id: '', course_id: '', expired_at: '' });
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <form onSubmit={(e) => {
              e.preventDefault();
              handleCreateUserService();
            }} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Course
                </label>
                <select
                  value={serviceFormData.course_id}
                  onChange={(e) => handleServiceInputChange('course_id', e.target.value)}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select a course</option>
                  {courses.map((course) => (
                    <option key={course.id} value={course.id}>
                      {course.title}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Expiration Date
                </label>
                <input
                  type="date"
                  value={serviceFormData.expired_at}
                  onChange={(e) => handleServiceInputChange('expired_at', e.target.value)}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Service will expire at 23:59:59 on the selected date
                </p>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowCreateServiceModal(false);
                    setServiceFormData({ user_id: '', course_id: '', expired_at: '' });
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={loading}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  {loading ? 'Creating...' : 'Create Service'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
      
      {/* Update Password Modal */}
      {showPasswordModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold text-gray-900">Update Password</h2>
              <button
                onClick={() => {
                  setShowPasswordModal(false);
                  setSelectedUserForPassword(null);
                  setNewPassword('');
                  setConfirmPassword('');
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {selectedUserForPassword && (
              <div className="mb-6 p-3 bg-blue-50 border border-blue-200 rounded-md">
                <p className="text-sm font-medium text-blue-900">
                  {selectedUserForPassword.first_name} {selectedUserForPassword.last_name}
                </p>
                <p className="text-sm text-blue-600">{selectedUserForPassword.email}</p>
              </div>
            )}

            <form onSubmit={(e) => {
              e.preventDefault();
              handleUpdatePassword();
            }} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  New Password
                </label>
                <div className="relative">
                  <input
                    type={showNewPassword ? "text" : "password"}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    minLength={6}
                    placeholder="Enter new password"
                    className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute inset-y-0 right-0 px-3 flex items-center text-gray-600 hover:text-gray-800"
                  >
                    {showNewPassword ? (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                      </svg>
                    ) : (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    )}
                  </button>
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  Password must be at least 6 characters long
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Confirm Password
                </label>
                <div className="relative">
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    minLength={6}
                    placeholder="Confirm new password"
                    className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute inset-y-0 right-0 px-3 flex items-center text-gray-600 hover:text-gray-800"
                  >
                    {showConfirmPassword ? (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                      </svg>
                    ) : (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    )}
                  </button>
                </div>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowPasswordModal(false);
                    setSelectedUserForPassword(null);
                    setNewPassword('');
                    setConfirmPassword('');
                    setShowNewPassword(false);
                    setShowConfirmPassword(false);
                  }}
                  disabled={updatingPassword}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={updatingPassword}
                  className="bg-yellow-600 hover:bg-yellow-700"
                >
                  {updatingPassword ? (
                    <>
                      <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2 inline-block"></div>
                      Updating...
                    </>
                  ) : (
                    'Update Password'
                  )}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Bulk Upload Modal */}
      {isBulkUploadModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-semibold text-gray-900">Bulk Upload Users</h2>
              <button
                onClick={() => {
                  if (!isBulkUploading) {
                    setIsBulkUploadModalOpen(false);
                    setBulkUploadFile(null);
                    setBulkUploadProgress({ current: 0, total: 0, status: '' });
                  }
                }}
                disabled={isBulkUploading}
                className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="space-y-6">
              {/* Instructions */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h3 className="font-semibold text-blue-900 mb-2">📋 Instructions</h3>
                <ol className="list-decimal list-inside space-y-1 text-sm text-blue-800">
                  <li>Download the Excel template using the "Download Template" button</li>
                  <li>Fill in the user information in the Excel file</li>
                  <li>Required fields: Username, Email, Password, First Name, Last Name</li>
                  <li>Optional fields: Role (default: student), Phone, Gender, Date of Birth, Address, Nationality, Education Level</li>
                  <li>Upload the completed Excel file below</li>
                </ol>
              </div>

              {/* Template Download Section */}
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold text-purple-900 mb-1">📥 Download Template</h3>
                    <p className="text-sm text-purple-700">
                      Start by downloading the template Excel file
                    </p>
                  </div>
                  <button
                    onClick={downloadExampleFile}
                    className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors"
                  >
                    Download
                  </button>
                </div>
              </div>

              {/* File Upload Section */}
              <div className="space-y-3">
                <label className="block">
                  <span className="text-sm font-medium text-gray-700 mb-2 block">
                    Upload Excel File
                  </span>
                  <input
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={handleBulkUploadFileChange}
                    disabled={isBulkUploading}
                    className="block w-full text-sm text-gray-500
                      file:mr-4 file:py-2 file:px-4
                      file:rounded-md file:border-0
                      file:text-sm file:font-semibold
                      file:bg-green-50 file:text-green-700
                      hover:file:bg-green-100
                      disabled:opacity-50 disabled:cursor-not-allowed"
                  />
                </label>
                
                {bulkUploadFile && (
                  <div className="flex items-center gap-2 text-sm text-gray-600 bg-gray-50 p-2 rounded">
                    <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span>Selected: {bulkUploadFile.name}</span>
                  </div>
                )}
              </div>

              {/* Progress Section */}
              {isBulkUploading && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-700 font-medium">{bulkUploadProgress.status}</span>
                    <span className="text-gray-600">
                      {bulkUploadProgress.current} / {bulkUploadProgress.total}
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2.5">
                    <div
                      className="bg-green-600 h-2.5 rounded-full transition-all duration-300"
                      style={{
                        width: `${(bulkUploadProgress.current / bulkUploadProgress.total) * 100}%`
                      }}
                    ></div>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex justify-end space-x-3 pt-4 border-t">
                <button
                  onClick={() => {
                    setIsBulkUploadModalOpen(false);
                    setBulkUploadFile(null);
                    setBulkUploadProgress({ current: 0, total: 0, status: '' });
                  }}
                  disabled={isBulkUploading}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleBulkUpload}
                  disabled={!bulkUploadFile || isBulkUploading}
                  className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                >
                  {isBulkUploading ? (
                    <>
                      <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full"></div>
                      Uploading...
                    </>
                  ) : (
                    <>
                      <span>📤</span> Upload Users
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
export default Users;